<script>
/**
 * @component ProviderDoctorList
 * @description Listado de prestadores (usuarios con rol Doctor).
 *              Búsqueda, paginación, acceso a crear/editar prestador via UserDetail.
 * @example <ProviderDoctorList />
 */
import pagination from "@/mixins/pagination";
import debounce from "debounce";
import userService from "@/services/user";
import inviteService from "@/services/invite";
import helperService from "@/services/helper";
import { mapGetters } from "vuex";
import Spinner from "@/components/ui/Spinner";

const DEBOUNCE_MS = 500;

export default {
  name: "ProviderDoctorList",

  mixins: [pagination],

  components: { Spinner },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      doctors: [],
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),

    /**
     * @description ID del User actualmente logueado. Usado para evitar
     *              que el admin se elimine/bloquee a sí mismo.
     */
    loggedUserId() {
      return this.$store.state.user._id;
    },
  },

  created() {
    this.fetchDoctors();
  },

  methods: {
    /**
     * @description Obtiene los usuarios con rol Doctor del backend
     */
    fetchDoctors() {
      this.isLoading = true;

      const query = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
      };

      if (this.searchInput) {
        query.searchInput = this.searchInput;
      }

      userService
        .getDoctors(query)
        .then((res) => {
          this.doctors = res.docs;
          this.pagination.total = res.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    debounceSearch: debounce(function () {
      this.pagination.currentPage = 1;
      this.pagination.offset = 0;
      this.fetchDoctors();
    }, DEBOUNCE_MS),

    onPageChange() {
      this.fetchDoctors();
    },

    /**
     * @description Navega al detalle del prestador (UserDetail en providerMode)
     * @param {string} id - ID del doctor
     */
    editDoctor(id) {
      helperService.callIfNoSelectedText(() =>
        this.$router.push({
          name: "providers-edit",
          params: { id },
        })
      );
    },

    /**
     * @description Wrapper para el dropdown del UC-REG-092: el `@command`
     *              entrega el doctor entero, pero `editDoctor` espera el id.
     * @param {Object} doctor
     */
    editDoctorCmd(doctor) {
      this.editDoctor(doctor._id);
    },

    /**
     * @description Devuelve el nombre completo del usuario
     * @param {Object} user - Usuario
     * @returns {string}
     */
    getFullName(user) {
      return helperService.getFullName(user);
    },

    /**
     * @description Devuelve las especialidades como texto separado por comas
     * @param {Object} doctor - Doctor con medicalSpecialties populadas
     * @returns {string}
     */
    getSpecialties(doctor) {
      if (!doctor.medicalSpecialties || !doctor.medicalSpecialties.length) {
        return "-";
      }
      return doctor.medicalSpecialties
        .map((s) => (typeof s === "object" ? s.name : s))
        .join(", ");
    },

    /**
     * @description Devuelve las instituciones como texto separado por comas
     * @param {Object} doctor - Doctor con institutions populadas
     * @returns {string}
     */
    getInstitutions(doctor) {
      if (!doctor.institutions || !doctor.institutions.length) {
        return "-";
      }
      return doctor.institutions
        .map((i) => (typeof i === "object" ? i.name : i))
        .join(", ");
    },

    /**
     * @description Devuelve el número de matrícula del doctor
     * @param {Object} doctor - Doctor
     * @returns {string}
     */
    getLicense(doctor) {
      return doctor.doctor?.license?.number || "-";
    },

    /**
     * @description UC-REG-092 — Bloquea el User del médico (reversible).
     *              No puede loguear hasta que se desbloquee. No toca
     *              historiales ni listados externos.
     * @param {Object} doctor
     */
    lockDoctor(doctor) {
      this.$confirm(
        `El médico "${this.getFullName(
          doctor
        )}" no podrá loguear hasta que lo desbloquees. ¿Continuar?`,
        "Bloquear médico",
        {
          confirmButtonText: "Bloquear",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      ).then(() => {
        userService
          .lock(doctor._id)
          .then(() => {
            this.$message({ type: "success", message: "Médico bloqueado" });
            this.fetchDoctors();
          })
          .catch(() => {
            this.$message({ type: "error", message: "No se pudo bloquear" });
          });
      });
    },

    /**
     * @description UC-REG-092 — Desbloquea el User del médico, volviéndolo
     *              activo para login.
     * @param {Object} doctor
     */
    unlockDoctor(doctor) {
      userService
        .unlock(doctor._id)
        .then(() => {
          this.$message({ type: "success", message: "Médico desbloqueado" });
          this.fetchDoctors();
        })
        .catch(() => {
          this.$message({ type: "error", message: "No se pudo desbloquear" });
        });
    },

    /**
     * @description UC-REG-092 — Soft-delete del User del médico. Mantiene
     *              el historial (ServiceRecords, recetas, auditorías) y
     *              libera username/email para reuso. No reversible desde
     *              el dashboard.
     * @param {Object} doctor
     */
    deleteDoctor(doctor) {
      this.$confirm(
        `"${this.getFullName(
          doctor
        )}" dejará de poder loguear y no aparecerá más en listados. El historial (recetas, auditorías, atenciones) se conserva. Esta acción no se puede deshacer.`,
        "Eliminar médico",
        {
          confirmButtonText: "Eliminar",
          cancelButtonText: "Cancelar",
          type: "warning",
          confirmButtonClass: "el-button--danger",
        }
      ).then(() => {
        userService
          .delete(doctor._id)
          .then(() => {
            this.$message({ type: "success", message: "Médico eliminado" });
            this.fetchDoctors();
          })
          .catch(() => {
            this.$message({ type: "error", message: "No se pudo eliminar" });
          });
      });
    },

    /**
     * @description UC-REG-092 — Reenvía la invitación de activación al
     *              médico (útil si el token caducó o el link no llegó).
     * @param {Object} doctor
     */
    resendInvitation(doctor) {
      if (!doctor.username) {
        this.$message({
          type: "warning",
          message: "El médico no tiene username asignado.",
        });
        return;
      }
      inviteService
        .sendInvite(doctor.username)
        .then(() => {
          this.$message({ type: "success", message: "Invitación reenviada" });
        })
        .catch(() => {
          this.$message({
            type: "error",
            message: "No se pudo reenviar la invitación",
          });
        });
    },

    /**
     * @description UC-REG-096 — Envía email de "blanquear clave" al médico.
     *              A diferencia de "Reenviar invitación", este flujo usa el
     *              flujo de password-reset estándar (no toca el verification
     *              token, no requiere que sea un user sin password). El
     *              médico recibe un email con link para fijar clave nueva.
     * @param {Object} doctor
     */
    resetPassword(doctor) {
      if (!doctor.email) {
        this.$message({
          type: "warning",
          message:
            "El médico no tiene email registrado — no se le puede enviar el blanqueo.",
        });
        return;
      }
      this.$confirm(
        `Se enviará un email a ${doctor.email} con un link para fijar una clave nueva. ¿Continuar?`,
        "Blanquear clave",
        {
          confirmButtonText: "Enviar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      ).then(() => {
        // UC-REG-097: pasamos _id en vez de username — endpoint admin-tool
        // que filtra por organization, exige auth y devuelve 404 si no
        // encuentra al user (a diferencia del endpoint público silencioso).
        userService
          .sendPasswordReset(doctor._id)
          .then(() => {
            this.$message({
              type: "success",
              message: "Email de blanqueo de clave enviado",
            });
          })
          .catch((err) => {
            const apiErr = err?.response?.data?.error;
            const message =
              apiErr === "UserHasNoEmail"
                ? "El médico no tiene email registrado."
                : apiErr === "UserNotFound"
                ? "Médico no encontrado en tu organización."
                : "No se pudo enviar el blanqueo.";
            this.$message({ type: "error", message });
          });
      });
    },
  },
};
</script>

<template lang="pug">
section
  header.headline
    .headline__actions
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-model="searchInput"
        @input="debounceSearch"
        placeholder="Buscar por nombre o apellido"
        :clearable="true"
        size="small"
      )
      router-link.button.button--navy(
        :to="{ name: 'providers-create' }"
        v-if="isAdmin"
      )
        micon(name="add_circle_outline").button__icon
        span.button__text Nuevo Prestador

  .box.box--with-subnav
    .box__content--stretch
      table
        thead
          tr
            th Nombre
            th Matrícula
            th Especialidades
            th Instituciones
            th Estado
            th(v-if="isAdmin").actions-col Acciones
        tbody
          spinner(v-if="isLoading")
          tr(
            v-else
            v-for="doctor in doctors"
            :key="doctor._id"
            @click="editDoctor(doctor._id)"
          )
            td {{ getFullName(doctor) }}
            td {{ getLicense(doctor) }}
            td {{ getSpecialties(doctor) }}
            td {{ getInstitutions(doctor) }}
            td
              span.badge(
                :class="doctor.lockedAt ? 'badge--danger' : 'badge--success'"
              ) {{ doctor.lockedAt ? 'Bloqueado' : 'Activo' }}
            //- UC-REG-092: acciones del admin sobre el User del médico.
            //- @click.stop evita que se dispare el editDoctor del row.
            td.actions-col(v-if="isAdmin" @click.stop)
              el-dropdown(trigger="click" @command="(cmd) => cmd(doctor)")
                button.button.button--action(type="button")
                  micon(name="more_horiz")
                el-dropdown-menu(slot="dropdown")
                  el-dropdown-item(:command="editDoctorCmd")
                    micon(name="edit")
                    | &nbsp;Editar
                  //- UC-REG-096: ambas opciones siempre visibles (antes
                  //- "Reenviar invitación" estaba escondido si ya tenía
                  //- password, lo que impedía ayudar a un médico atascado).
                  //- "Blanquear clave" envía el flujo password-reset
                  //- estándar para que fije una nueva.
                  el-dropdown-item(:command="resendInvitation")
                    micon(name="email")
                    | &nbsp;Reenviar invitación
                  el-dropdown-item(:command="resetPassword")
                    micon(name="refresh")
                    | &nbsp;Blanquear clave
                  el-dropdown-item(
                    v-if="loggedUserId !== doctor._id && !doctor.lockedAt"
                    divided
                    :command="lockDoctor"
                  )
                    micon(name="lock")
                    | &nbsp;Bloquear
                  el-dropdown-item(
                    v-if="loggedUserId !== doctor._id && doctor.lockedAt"
                    :command="unlockDoctor"
                  )
                    micon(name="lock_open")
                    | &nbsp;Desbloquear
                  el-dropdown-item(
                    v-if="loggedUserId !== doctor._id"
                    divided
                    :command="deleteDoctor"
                  )
                    span.action-danger
                      micon(name="delete_forever")
                      | &nbsp;Eliminar

    pagination(
      :isLoading="isLoading"
      :limit="pagination.limit"
      :total="pagination.total"
      @pagination="setPagination"
    )
</template>

<style lang="scss" scoped>
.headline__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search__input {
  width: 280px;
}

tbody td:hover {
  cursor: pointer;
}

// UC-REG-092: columna de acciones — botón compacto, sin pisar el click
// del row.
.actions-col {
  width: 80px;
  text-align: center;
}

.button--action {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;

  &:hover {
    background: #f1f5f9;
  }
}

.action-danger {
  color: #b03434;
  display: inline-flex;
  align-items: center;
}

// UC-REG-097: alinear iconos + texto en cada item del dropdown de acciones.
// Element UI deja el icono "flotando" sobre la línea base del texto.
// UC-REG-098: además, los items con prop `divided` tienen un ::before
// que rompe el padding lateral y empujaba el icono fuera del cuadro.
// Forzamos padding y quitamos el ::before del divider — la separación
// visual queda dada por el border-top + margin-top que ya aplica el item.
:deep(.el-dropdown-menu__item) {
  display: flex !important;
  align-items: center;
  gap: 8px;
  line-height: 1.4;
  padding: 0 16px;

  .micon,
  i {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
}

:deep(.el-dropdown-menu__item--divided) {
  margin-top: 4px;
  padding-top: 0;
  border-top: 1px solid #ebeef5;
}

:deep(.el-dropdown-menu__item--divided)::before {
  display: none;
}
</style>
